import { useState, useEffect, useCallback } from "react";
import Sidebar, { dispatchSidebarUpdate } from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { useAuth } from "../Auth-Context/AuthContext";
import { useCompanies } from "../Auth-Context/CompanyContext";
import { useToast } from "../Auth-Context/ToastContext";
import ContactDrawer from "../components/ContactDrawer";
import ConfirmArchiveModal from "../components/ConfirmArchiveModal";
import {
  listContacts, archiveContact, unarchiveContact, getInteractionsByContact,
} from "../api/services";
import {
  Plus, Pencil, Trash2, Eye, Archive, ArchiveRestore, Building2,
  ChevronLeft, ChevronRight, Loader, X, Phone, Mail, Calendar, FileText,
} from "lucide-react";

const PAGE_SIZE = 10;
const REASON_TRUNCATE_LENGTH = 10;

const getName = (c) => c?.company || c?.company_name || "this company";

const getArchiveReason = (c) => c?.archiveReason || "";

// Matches the API response field name (archivedAt)
const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });


  
};

const ARCHIVE_ERROR_COPY = {
  archive: {
    forbidden: "You don't have permission to archive companies.",
    notFound: "This company could not be found. It may already be archived or deleted.",
    fallback: "Failed to archive company. Please try again.",
  },
  unarchive: {
    forbidden: "Only a Sales Manager can unarchive companies.",
    notFound: "This company could not be found. It may already be unarchived or deleted.",
    fallback: "Failed to unarchive company. Please try again.",
  },
};

const getArchiveErrorMessage = (err, action) => {
  const copy = ARCHIVE_ERROR_COPY[action];
  const response = err?.response;

  if (response) {
    const { status, data } = response;
    if (status === 400 || status === 422) {
      const validation = data?.validationErrors
        ? Object.values(data.validationErrors).join(" ")
        : "";
      return validation || data?.message || copy.fallback;
    }
    if (status === 401) return "Your session has expired. Please log in again.";
    if (status === 403) return copy.forbidden;
    if (status === 404) return copy.notFound;
    if (status >= 500) return "Something went wrong on our side. Please try again.";
    return data?.message || copy.fallback;
  }

  if (err?.request || err?.message === "Network Error") {
    return "Network error. Please check your connection and try again.";
  }

  return copy.fallback;
};

// Reason cell: plain text when short, a clickable truncated button when long.
// `openReasonModal` receives the full { companyName, reason } to display.
function ArchiveReasonCell({ company, openReasonModal, className = "" }) {
  const reason = getArchiveReason(company);
  if (!reason) {
    return <span className={className}>—</span>;
  }
  if (reason.length <= REASON_TRUNCATE_LENGTH) {
    return <span className={className}>{reason}</span>;
  }
  return (
    <button
      onClick={() => openReasonModal({ companyName: getName(company), reason })}
      className={`${className} text-left text-[#009DB9] hover:underline`}
      title="Click to see full reason"
    >
      {reason.slice(0, REASON_TRUNCATE_LENGTH).trimEnd()}…
    </button>
  );
}

function ArchiveReasonModal({ data, onClose }) {
  if (!data) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-sm mx-4 overflow-hidden bg-white shadow-2xl rounded-2xl">
        <div className="flex items-start justify-between px-5 py-4 border-b border-neutral-100">
          <div>
            <h2 className="text-[14px] font-bold text-neutral-900">Archive Reason</h2>
            <p className="text-[12px] text-neutral-400 mt-0.5">{data.companyName}</p>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center flex-shrink-0 rounded-lg w-7 h-7 text-neutral-400 hover:bg-neutral-100"
          >
            <X size={15} />
          </button>
        </div>
        <div className="px-5 py-4">
          <p className="text-[13px] text-neutral-700 leading-relaxed whitespace-pre-wrap break-words">
            {data.reason}
          </p>
        </div>
        <div className="px-5 pb-5">
          <button
            onClick={onClose}
            className="w-full py-2 rounded-xl text-[13px] font-semibold border border-neutral-200 text-neutral-600 hover:bg-neutral-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Archived company → read-only interaction history ──────────────────────
// Only interactions, nothing else (no edit, no other company tabs). Lets the
// Sales Manager look back at everything that was logged before archiving.
const INTERACTION_TYPE_STYLE = {
  CALL:    { bg: "#dcfce7", color: "#16a34a", icon: Phone },
  EMAIL:   { bg: "#dbeafe", color: "#1d4ed8", icon: Mail },
  MEETING: { bg: "#ede9fe", color: "#7c3aed", icon: Calendar },
  NOTE:    { bg: "#fef3c7", color: "#d97706", icon: FileText },
};

function ArchivedInteractionsModal({ open, company, onClose }) {
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !company?.id) return;
    let cancelled = false;

    const fetchInteractions = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await getInteractionsByContact(company.id);
        const list = Array.isArray(res) ? res : res.content || [];
        if (!cancelled) setInteractions(list);
      } catch (err) {
        console.error("Failed to fetch interactions:", err);
        if (!cancelled) setError("Failed to load interactions. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchInteractions();
    return () => { cancelled = true; };
  }, [open, company?.id]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.4)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="flex flex-col w-full max-w-lg mx-4 bg-white shadow-2xl rounded-2xl"
        style={{ maxHeight: "85vh" }}
      >
        <div className="flex items-start justify-between flex-shrink-0 px-5 py-4 border-b border-neutral-100">
          <div>
            <h2 className="text-[14px] font-bold text-neutral-900">Interaction History</h2>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-[12px] text-neutral-500">{getName(company)}</span>
              <span className="px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 rounded-full bg-amber-50 border border-amber-200">
                Archived
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center flex-shrink-0 rounded-lg w-7 h-7 text-neutral-400 hover:bg-neutral-100"
          >
            <X size={15} />
          </button>
        </div>

        <div className="flex-1 px-5 py-4 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader size={24} className="animate-spin text-[#009DB9]" />
            </div>
          ) : error ? (
            <div className="p-3 text-[12px] text-red-600 border border-red-200 rounded-lg bg-red-50">
              {error}
            </div>
          ) : interactions.length === 0 ? (
            <div className="py-10 text-center text-[13px] text-neutral-400">
              No interactions were logged for this company.
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {interactions.map((log) => {
                const s = INTERACTION_TYPE_STYLE[log.type] || INTERACTION_TYPE_STYLE.NOTE;
                const Icon = s.icon;
                const occurredAt = log.occurredAt ? new Date(log.occurredAt) : null;
                return (
                  <div key={log.id} className="flex gap-3 p-3.5 bg-neutral-50 border border-neutral-100 rounded-xl">
                    <div
                      className="flex items-center justify-center flex-shrink-0 w-8 h-8 mt-0.5 rounded-lg"
                      style={{ background: s.bg }}
                    >
                      <Icon size={13} style={{ color: s.color }} strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-[13px] font-semibold text-neutral-800">
                          {log.subject || log.type}
                        </span>
                        <span className="text-[11px] text-neutral-400 flex-shrink-0">
                          {occurredAt ? occurredAt.toLocaleDateString() : "No date"}
                          {occurredAt ? ` · ${occurredAt.toLocaleTimeString()}` : ""}
                        </span>
                      </div>
                      {(log.userName || log.userId) && (
                        <div className="text-[10px] text-neutral-400 mt-0.5">
                          Logged by {log.userName || log.userId}
                        </div>
                      )}
                      {log.notes && (
                        <p className="text-[12px] text-neutral-600 mt-1.5 whitespace-pre-wrap break-words">
                          {log.notes}
                        </p>
                      )}
                      {log.durationMinutes && (
                        <div className="text-[11px] text-neutral-400 mt-1">
                          {log.durationMinutes} minutes
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex-shrink-0 px-5 py-4 border-t border-neutral-100">
          <button
            onClick={onClose}
            className="w-full py-2 rounded-xl text-[13px] font-semibold border border-neutral-200 text-neutral-600 hover:bg-neutral-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmDeleteModal({ open, onClose, onConfirm, companyName, loading }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-sm mx-4 overflow-hidden bg-white shadow-2xl rounded-2xl">
        <div className="flex flex-col items-center px-6 pb-4 text-center pt-7">
          <div className="flex items-center justify-center mb-4 w-14 h-14 rounded-2xl bg-red-50">
            <Trash2 size={24} className="text-red-500" strokeWidth={2} />
          </div>
          <h2 className="text-[16px] font-bold text-neutral-900">Delete Contact?</h2>
          <p className="text-[13px] text-neutral-500 mt-1.5 leading-relaxed">
            You are about to delete{" "}
            <span className="font-semibold text-neutral-800">"{companyName}"</span>.
            <br />This action cannot be undone.
          </p>
        </div>
        <div className="flex gap-2.5 px-6 pb-6 pt-2">
          <button onClick={onClose}
            className="flex-1 h-10 rounded-xl text-[13px] font-semibold border border-neutral-200 text-neutral-600 hover:bg-neutral-50 transition-colors">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 h-10 rounded-xl text-[13px] font-semibold text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ background: "#ef4444" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#dc2626")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#ef4444")}
          >
            {loading ? <Loader size={14} className="animate-spin" /> : "Yes, Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Contacts() {
  const { user, loading: authLoading } = useAuth();
  const { addCompany, updateCompany, deleteCompany } = useCompanies();
  const toast = useToast();

  const isManager = user?.role === "MANAGER";

  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchVal, setSearchVal] = useState("");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ── Archive / unarchive ──
  const [view, setView] = useState("active"); // "active" | "archived"

  // Archived list — loaded from GET /api/contacts?archived=true
  const [archivedContacts, setArchivedContacts] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [archivedPage, setArchivedPage] = useState(0);
  const [archivedTotalPages, setArchivedTotalPages] = useState(0);
  const [archivedTotalElements, setArchivedTotalElements] = useState(0);

  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState("");
  const [unarchiveTarget, setUnarchiveTarget] = useState(null);
  const [unarchiving, setUnarchiving] = useState(false);
  const [unarchiveError, setUnarchiveError] = useState("");

  // Reason modal (archived view only)
  const [reasonModalData, setReasonModalData] = useState(null);

  // Read-only interaction history modal (archived view only)
  const [interactionsTarget, setInteractionsTarget] = useState(null);

  // Sales Reps can never see the archived view
  const isArchivedView = isManager && view === "archived";

  const [drawer, setDrawer] = useState({ open: false, mode: "add", contact: null });

  const openAdd  = ()  => setDrawer({ open: true, mode: "add",  contact: null });
  const openEdit = (c) => setDrawer({ open: true, mode: "edit", contact: c    });
  const openView = (c) => setDrawer({ open: true, mode: "view", contact: c    });
  const closeDrawer = () => setDrawer({ open: false, mode: "add", contact: null });

  // ── Fetch: active list ──
  const fetchContacts = useCallback(async (pageNum = 0, search = "") => {
    setLoading(true);
    try {
      const params = {
        page: pageNum,
        size: PAGE_SIZE,
        archived: false,
      };
      if (search.trim()) {
        params.search = search.trim();
      }
      const response = await listContacts(params);
      setContacts(response.content || []);
      setTotalPages(response.totalPages || 0);
      setTotalElements(response.totalElements || 0);
    } catch (err) {
      console.error("Failed to fetch contacts:", err);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Fetch: archived list (manager only) — client-side newest-archived-first ──
  const fetchArchivedContacts = useCallback(async (pageNum = 0, search = "") => {
    setArchivedLoading(true);
    try {
      const params = {
        page: pageNum,
        size: PAGE_SIZE,
        archived: true,
      };
      if (search.trim()) {
        params.search = search.trim();
      }
      const response = await listContacts(params);

      // Client-side sort: newest archived first (within the current page).
      const sorted = [...(response.content || [])].sort((a, b) => {
        const aT = a.archivedAt ? new Date(a.archivedAt).getTime() : 0;
        const bT = b.archivedAt ? new Date(b.archivedAt).getTime() : 0;
        return bT - aT;
      });

      setArchivedContacts(sorted);
      setArchivedTotalPages(response.totalPages || 0);
      setArchivedTotalElements(response.totalElements || 0);
    } catch (err) {
      console.error("Failed to fetch archived contacts:", err);
      setArchivedContacts([]);
    } finally {
      setArchivedLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchContacts(page, searchVal);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (user && !isArchivedView) {
      fetchContacts(page, searchVal);
    }
  }, [page, searchVal, user, fetchContacts, isArchivedView]);

  useEffect(() => {
    if (user && isArchivedView) {
      fetchArchivedContacts(archivedPage, searchVal);
    }
  }, [archivedPage, searchVal, user, isArchivedView, fetchArchivedContacts]);

  const handleSearchChange = (value) => {
    setSearchVal(value);
    setPage(0);
    setArchivedPage(0);
  };

  const handleViewChange = (nextView) => {
    if (nextView === view) return;
    setSearchVal("");
    setPage(0);
    setArchivedPage(0);
    setView(nextView);
    // The effects above will trigger the correct fetch on view change.
  };

  const goToNextPage = () => {
    if (isArchivedView) {
      if (archivedPage < archivedTotalPages - 1) setArchivedPage(archivedPage + 1);
    } else {
      if (page < totalPages - 1) setPage(page + 1);
    }
  };

  const goToPrevPage = () => {
    if (isArchivedView) {
      if (archivedPage > 0) setArchivedPage(archivedPage - 1);
    } else {
      if (page > 0) setPage(page - 1);
    }
  };

  const goToPage = (newPage) => {
    if (isArchivedView) {
      if (newPage >= 0 && newPage < archivedTotalPages) setArchivedPage(newPage);
    } else {
      if (newPage >= 0 && newPage < totalPages) setPage(newPage);
    }
  };

  const handleFormSubmit = async (data) => {
    if (drawer.mode === "edit" && drawer.contact) {
      await updateCompany(drawer.contact.id, data);
    } else {
      await addCompany(data);
      await fetchContacts(page, searchVal);
      dispatchSidebarUpdate({ contacts: totalElements + 1 });
    }
    closeDrawer();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteCompany(deleteTarget.id);
      await fetchContacts(page, searchVal);
      dispatchSidebarUpdate({ contacts: Math.max(0, totalElements - 1) });
      setDeleteTarget(null);
    } catch (error) {
      console.error("Failed to delete contact:", error);
      alert("Failed to delete contact. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  // ── Archive ──
  const openArchive = (c) => {
    setArchiveError("");
    setArchiveTarget(c);
  };

  const closeArchive = () => {
    if (archiving) return;
    setArchiveTarget(null);
    setArchiveError("");
  };

  const handleArchive = async (reason) => {
    if (!archiveTarget) return;
    const target = archiveTarget;
    setArchiving(true);
    setArchiveError("");
    try {
      await archiveContact({
        id: target.id,
        reason: reason?.trim() || undefined,
      });

      dispatchSidebarUpdate({ contacts: Math.max(0, totalElements - 1) });
      toast.success(`"${getName(target)}" was moved to archived companies.`);
      setArchiveTarget(null);

      // Refresh the active list; step back a page if this one just emptied out
      if (contacts.length === 1 && page > 0) {
        setPage(page - 1);
      } else {
        await fetchContacts(page, searchVal);
      }
    } catch (err) {
      console.error("Failed to archive company:", err);
      setArchiveError(getArchiveErrorMessage(err, "archive"));
    } finally {
      setArchiving(false);
    }
  };

  // ── Unarchive (manager only) ──
  const openUnarchive = (c) => {
    if (!isManager) return;
    setUnarchiveError("");
    setUnarchiveTarget(c);
  };

  const closeUnarchive = () => {
    if (unarchiving) return;
    setUnarchiveTarget(null);
    setUnarchiveError("");
  };

  const handleUnarchive = async () => {
    if (!unarchiveTarget || !isManager) return;
    const target = unarchiveTarget;
    setUnarchiving(true);
    setUnarchiveError("");
    try {
      await unarchiveContact({ id: target.id });

      dispatchSidebarUpdate({ contacts: totalElements + 1 });
      toast.success(`"${getName(target)}" was restored to active companies.`);
      setUnarchiveTarget(null);

      // Refresh the archived list; step back a page if this one just emptied out
      if (archivedContacts.length === 1 && archivedPage > 0) {
        setArchivedPage(archivedPage - 1);
      } else {
        await fetchArchivedContacts(archivedPage, searchVal);
      }
    } catch (err) {
      console.error("Failed to unarchive company:", err);
      setUnarchiveError(getArchiveErrorMessage(err, "unarchive"));
    } finally {
      setUnarchiving(false);
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-neutral-50">
        <div className="flex flex-col items-center gap-3">
          <Loader size={32} className="animate-spin text-[#009DB9]" />
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Row data: real fetch for both views
  const rows = isArchivedView ? archivedContacts : contacts;
  const listLoading = isArchivedView ? archivedLoading : loading;
  const currentPage = isArchivedView ? archivedPage : page;
  const currentTotalPages = isArchivedView ? archivedTotalPages : totalPages;
  const currentTotalElements = isArchivedView ? archivedTotalElements : totalElements;

  const tableHeaders = isArchivedView
    ? ["#", "Company Name", "Contact Person", "Phone", "Email", "Archived On", "Reason", "Actions"]
    : ["#", "Company Name", "Contact Person", "Phone", "Email", "Actions"];

  const emptyMessage = isArchivedView
    ? (searchVal ? "No archived companies match your search." : "No archived companies yet.")
    : (searchVal ? "No companies found matching your search." : "No companies found.");

  return (
    <div className="flex h-screen overflow-hidden bg-neutral-50">
      <Sidebar />

      <div className="flex flex-col flex-1 overflow-hidden">
        <Topbar
          title="Companies"
          subtitle={isArchivedView ? `${currentTotalElements} archived companies` : `${currentTotalElements} contacts`}
          search={{ 
            value: searchVal, 
            onChange: handleSearchChange, 
            placeholder: isArchivedView ? "Search archived companies…" : "Search company name…" 
          }}
          actions={isArchivedView ? [] : [{ label: "Add Company", icon: Plus, onClick: openAdd }]}
        />

        <main className="flex-1 px-4 py-4 overflow-y-auto md:py-6 md:px-7">

          {/* Active / Archived switch — Sales Manager only */}
          {isManager && (
            <div
              className="inline-flex p-1 mb-4 bg-white border rounded-xl border-neutral-100"
              role="tablist"
              aria-label="Company list view"
            >
              {[
                { id: "active",   label: "Active",   icon: Building2 },
                { id: "archived", label: "Archived", icon: Archive   },
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  role="tab"
                  aria-selected={view === id}
                  onClick={() => handleViewChange(id)}
                  className="flex items-center gap-1.5 px-3.5 h-8 rounded-lg text-[12px] font-semibold transition-colors"
                  style={view === id ? { background: "#009DB9", color: "#fff" } : { color: "#737373" }}
                >
                  <Icon size={13} />
                  {label}
                </button>
              ))}
            </div>
          )}

          {isArchivedView && (
            <div className="flex items-start gap-2 px-4 py-3 mb-4 text-[12px] text-amber-800 border border-amber-200 rounded-xl bg-amber-50">
              <Archive size={14} className="flex-shrink-0 mt-0.5" />
              <p>
                These companies are archived and hidden from the active companies list.
                Unarchive a company to restore it.
              </p>
            </div>
          )}

          <div className="bg-white border rounded-2xl border-neutral-100" style={{ boxShadow: "0 1px 6px rgba(0,0,0,0.05)" }}>

            <div className="px-3 md:px-5">
              {listLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader size={32} className="animate-spin text-[#009DB9]" />
                </div>
              ) : (
                <>
                  <div className="hidden overflow-x-auto md:block">
                    <table className={`w-full ${isArchivedView ? "min-w-[1100px]" : "min-w-[800px]"}`}>
                      <thead>
                        <tr className="border-b border-neutral-100">
                          {tableHeaders.map((h, i) => (
                            <th key={h}
                              className={`py-3 text-[11px] font-semibold text-neutral-400 uppercase tracking-wider text-left ${i === 0 ? "w-8" : ""}`}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.length === 0 && (
                          <tr>
                            <td colSpan={tableHeaders.length} className="py-12 text-center text-[13px] text-neutral-400">
                              {emptyMessage}
                            </td>
                          </tr>
                        )}
                        {rows.map((c, i) => {
                          const rowNum = (isArchivedView ? archivedPage : page) * PAGE_SIZE + i + 1;
                          const companyName = c.company || c.company_name || '—';
                          const contactPerson = `${c.firstName || ''} ${c.lastName || ''}`.trim() || '—';
                          return (
                            <tr key={c.id} className="transition-colors border-b border-neutral-100 last:border-0 hover:bg-neutral-50/60">
                              <td className="py-3 text-[12px] text-neutral-400 font-medium">
                                {String(rowNum).padStart(2, "0")}
                              </td>
                              <td className="py-3 pr-4">
                                <div className="flex items-center gap-2.5">
                                  <div className="w-8 h-8 rounded-lg flex items-center justify-center text-[11px] font-bold flex-shrink-0"
                                    style={{ background: c.logoColor?.bg || "#e0f2fe", color: c.logoColor?.color || "#0284c7" }}>
                                    {c.logo || companyName.substring(0, 2).toUpperCase()}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <div className="text-[13px] font-semibold text-neutral-900">{companyName}</div>
                                    {isArchivedView && (
                                      <span className="px-2 py-0.5 text-[10px] font-semibold text-amber-700 rounded-full bg-amber-50 border border-amber-200">
                                        Archived
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 text-[12px] text-neutral-500 pr-4">
                                {contactPerson}
                              </td>
                              <td className="py-3 text-[12px] text-neutral-500 tabular-nums pr-4">{c.phone || "—"}</td>
                              <td className="py-3 text-[12px] pr-4">
                                <a href={`mailto:${c.email}`} className="text-neutral-500 hover:text-[#009DB9] transition-colors">
                                  {c.email || "—"}
                                </a>
                              </td>
                              {isArchivedView && (
                                <>
                                  <td className="py-3 text-[12px] text-neutral-500 pr-4 whitespace-nowrap">
                                    {formatDate(c.archivedAt)}
                                  </td>
                                  <td className="py-3 text-[12px] pr-4 max-w-[200px]">
                                    <ArchiveReasonCell
                                      company={c}
                                      openReasonModal={setReasonModalData}
                                      className="text-neutral-500"
                                    />
                                  </td>
                                </>
                              )}
                              <td className="py-3">
                                {isArchivedView ? (
                                  <div className="flex items-center gap-1.5">
                                    <button onClick={() => setInteractionsTarget(c)}
                                      className="flex items-center justify-center text-purple-400 transition-colors rounded-lg w-7 h-7 hover:bg-purple-50" title="View Interactions">
                                      <Eye size={13} />
                                    </button>
                                    <button onClick={() => openUnarchive(c)}
                                      className="flex items-center gap-1.5 px-2.5 h-7 rounded-lg text-[11px] font-semibold text-[#009DB9] bg-sky-50 hover:bg-sky-100 transition-colors">
                                      <ArchiveRestore size={13} /> Unarchive
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    <button onClick={() => openEdit(c)}
                                      className="w-7 h-7 rounded-lg flex items-center justify-center text-[#009DB9] hover:bg-sky-50 transition-colors" title="Edit">
                                      <Pencil size={13} />
                                    </button>
                                    <button onClick={() => openArchive(c)}
                                      className="flex items-center justify-center transition-colors rounded-lg w-7 h-7 text-amber-500 hover:bg-amber-50" title="Archive">
                                      <Archive size={13} />
                                    </button>
                                    <button onClick={() => setDeleteTarget(c)}
                                      className="flex items-center justify-center text-red-400 transition-colors rounded-lg w-7 h-7 hover:bg-red-50" title="Delete">
                                      <Trash2 size={13} />
                                    </button>
                                    <button onClick={() => openView(c)}
                                      className="flex items-center justify-center text-purple-400 transition-colors rounded-lg w-7 h-7 hover:bg-purple-50" title="View">
                                      <Eye size={13} />
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="space-y-3 md:hidden">
                    {rows.length === 0 && (
                      <div className="py-12 text-center text-[13px] text-neutral-400">
                        {emptyMessage}
                      </div>
                    )}
                    {rows.map((c) => {
                      const companyName = c.company || c.company_name || '—';
                      const contactPerson = `${c.firstName || ''} ${c.lastName || ''}`.trim() || '—';
                      return (
                        <div key={c.id} className="p-4 space-y-3 bg-white border border-neutral-100 rounded-xl">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2.5">
                              <div className="w-10 h-10 rounded-lg flex items-center justify-center text-[11px] font-bold flex-shrink-0"
                                style={{ background: c.logoColor?.bg || "#e0f2fe", color: c.logoColor?.color || "#0284c7" }}>
                                {c.logo || companyName.substring(0, 2).toUpperCase()}
                              </div>
                              <div>
                                <div className="text-[14px] font-semibold text-neutral-900">{companyName}</div>
                                <div className="text-[11px] text-neutral-400">{contactPerson}</div>
                              </div>
                            </div>
                            {isArchivedView && (
                              <span className="px-2 py-0.5 text-[10px] font-semibold text-amber-700 rounded-full bg-amber-50 border border-amber-200">
                                Archived
                              </span>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2 text-[12px]">
                            {isArchivedView && (
                              <>
                                <div className="col-span-2">
                                  <span className="text-neutral-400">Archived On:</span>
                                  <span className="ml-1 text-neutral-600">{formatDate(c.archivedAt)}</span>
                                </div>
                                <div className="col-span-2">
                                  <span className="text-neutral-400">Reason:</span>{" "}
                                  <ArchiveReasonCell
                                    company={c}
                                    openReasonModal={setReasonModalData}
                                    className="inline text-neutral-600"
                                  />
                                </div>
                              </>
                            )}
                            <div className="col-span-2">
                              <span className="text-neutral-400">Phone:</span>
                              <span className="ml-1 text-neutral-600">{c.phone || "—"}</span>
                            </div>
                            <div className="col-span-2">
                              <span className="text-neutral-400">Email:</span>
                              <a href={`mailto:${c.email}`} className="ml-1 text-[#009DB9] hover:underline">
                                {c.email || "—"}
                              </a>
                            </div>
                          </div>
                          
                          {isArchivedView ? (
                            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-neutral-100">
                              <button onClick={() => setInteractionsTarget(c)}
                                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-purple-500 bg-purple-50 hover:bg-purple-100 transition-colors text-[12px] font-medium">
                                <Eye size={13} /> Interactions
                              </button>
                              <button onClick={() => openUnarchive(c)}
                                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[#009DB9] bg-sky-50 hover:bg-sky-100 transition-colors text-[12px] font-medium">
                                <ArchiveRestore size={13} /> Unarchive
                              </button>
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-neutral-100">
                              <button onClick={() => openEdit(c)}
                                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[#009DB9] bg-sky-50 hover:bg-sky-100 transition-colors text-[12px] font-medium">
                                <Pencil size={13} /> Edit
                              </button>
                              <button onClick={() => openArchive(c)}
                                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-amber-600 bg-amber-50 hover:bg-amber-100 transition-colors text-[12px] font-medium">
                                <Archive size={13} /> Archive
                              </button>
                              <button onClick={() => setDeleteTarget(c)}
                                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-red-500 bg-red-50 hover:bg-red-100 transition-colors text-[12px] font-medium">
                                <Trash2 size={13} /> Delete
                              </button>
                              <button onClick={() => openView(c)}
                                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-purple-500 bg-purple-50 hover:bg-purple-100 transition-colors text-[12px] font-medium">
                                <Eye size={13} /> View
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {!listLoading && currentTotalElements > 0 && (
              <div className="flex flex-col gap-3 px-3 py-3.5 border-t border-neutral-100 md:flex-row md:items-center md:justify-between md:px-5">
                <span className="text-[12px] text-neutral-400 text-center md:text-left">
                  Showing {rows.length} of {currentTotalElements} companies
                </span>
                <div className="flex items-center justify-center gap-1">
                  <button 
                    onClick={goToPrevPage}
                    disabled={currentPage === 0}
                    className="flex items-center justify-center rounded-lg w-7 h-7 text-neutral-400 hover:bg-neutral-100 disabled:opacity-30"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <div className="flex gap-1 max-w-[200px] overflow-x-auto">
                    {Array.from({ length: Math.min(currentTotalPages, 10) }, (_, i) => i).map((p) => (
                      <button 
                        key={p} 
                        onClick={() => goToPage(p)}
                        className="w-7 h-7 rounded-lg text-[12px] font-medium transition-colors flex-shrink-0"
                        style={currentPage === p ? { background: "#009DB9", color: "#fff" } : { color: "#737373" }}
                      >
                        {p + 1}
                      </button>
                    ))}
                  </div>
                  <button 
                    onClick={goToNextPage}
                    disabled={currentPage === currentTotalPages - 1}
                    className="flex items-center justify-center rounded-lg w-7 h-7 text-neutral-400 hover:bg-neutral-100 disabled:opacity-30"
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      <ContactDrawer
        open={drawer.open}
        mode={drawer.mode}
        contact={drawer.contact}
        onClose={closeDrawer}
        onSubmit={handleFormSubmit}
        onArchive={(c) => { closeDrawer(); openArchive(c); }}
        onAddContactPerson={async (contactId, personData) => {
          console.log("📤 Adding person to contact:", contactId);
          try {
            const { addContactPerson, getContactPersons } = await import("../api/services");
            
            const result = await addContactPerson({
              contactId: contactId,
              ...personData
            });
            console.log("✅ Person added:", result);
            
            const updatedPersons = await getContactPersons({ contactId: contactId });
            console.log("📋 Updated persons:", updatedPersons);
            
            setDrawer(prev => {
              const updatedContact = {
                ...prev.contact,
                contactPersons: updatedPersons || []
              };
              console.log("🔄 Updated contact:", updatedContact);
              return {
                ...prev,
                contact: updatedContact
              };
            });
            
            await fetchContacts(page, searchVal);
            
            return result;
          } catch (error) {
            console.error("❌ Error:", error);
            throw error;
          }
        }}
      />

      <ConfirmDeleteModal
        open={!!deleteTarget}
        companyName={deleteTarget?.company || deleteTarget?.company_name}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        loading={deleting}
      />

      <ConfirmArchiveModal
        open={!!archiveTarget}
        mode="archive"
        companyName={getName(archiveTarget)}
        loading={archiving}
        error={archiveError}
        onClose={closeArchive}
        onConfirm={handleArchive}
      />

      <ConfirmArchiveModal
        open={!!unarchiveTarget}
        mode="unarchive"
        companyName={getName(unarchiveTarget)}
        loading={unarchiving}
        error={unarchiveError}
        onClose={closeUnarchive}
        onConfirm={handleUnarchive}
      />

      <ArchiveReasonModal
        data={reasonModalData}
        onClose={() => setReasonModalData(null)}
      />

      <ArchivedInteractionsModal
        open={!!interactionsTarget}
        company={interactionsTarget}
        onClose={() => setInteractionsTarget(null)}
      />
    </div>
  );
}